import React, { useState, useEffect } from 'react';
import azureDevOpsService from './services/azureDevOpsService';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const AREA_PATH = 'Fabrique\\Tribus\\Tribu Argonos';

function lastWeekDate() {
  return new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
}

export default function KpiWorkItemsCompleted() {
  const today = new Date().toISOString().split('T')[0];

  const [startDate, setStartDate] = useState(lastWeekDate());
  const [endDate, setEndDate] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function handleCalculate() {
    setLoading(true);
    setError(null);
    try {
      const result = await azureDevOpsService.getCompletedWorkItems(startDate, endDate, AREA_PATH);
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { handleCalculate(); }, []);

  const chartData = data ? [
    { label: 'PBI',            count: data.pbiCount || 0,            color: '#3b82f6' },
    { label: 'Demande Client', count: data.demandeClientCount || 0,  color: '#14b8a6' },
    { label: 'Bug',            count: data.bugCount || 0,            color: '#ef4444' },
    { label: 'Dette Technique',count: data.detteTechniqueCount || 0, color: '#f97316' },
  ] : [];

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="mb-4">
        <h2 className="text-xl font-semibold text-gray-900">Work Items Complétés</h2>
        <p className="text-sm text-gray-500 mt-0.5">Tribu Argonos · PBI, Bugs, Demandes Client, Dettes Techniques</p>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-end gap-3 mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
            Date début
          </label>
          <input
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
            Date fin
          </label>
          <input
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <button
          onClick={handleCalculate}
          disabled={loading}
          className="px-5 py-1.5 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors shadow-sm"
        >
          {loading ? 'Chargement…' : 'Calculer'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
          Erreur : {error}
        </div>
      )}

      {loading && (
        <div className="flex flex-col items-center justify-center h-36 text-gray-400 gap-3">
          <svg className="animate-spin h-7 w-7 text-blue-500" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span className="text-sm">Chargement…</span>
        </div>
      )}

      {data && !loading && (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            <div className="bg-blue-50 p-4 rounded-xl border-2 border-blue-200">
              <div className="text-xs font-semibold text-blue-500 uppercase tracking-wide mb-1">PBI</div>
              <div className="text-4xl font-bold text-blue-700">{data.pbiCount || 0}</div>
            </div>
            <div className="bg-teal-50 p-4 rounded-xl border-2 border-teal-200">
              <div className="text-xs font-semibold text-teal-500 uppercase tracking-wide mb-1">Demandes Client</div>
              <div className="text-4xl font-bold text-teal-700">{data.demandeClientCount || 0}</div>
            </div>
            <div className="bg-red-50 p-4 rounded-xl border-2 border-red-200">
              <div className="text-xs font-semibold text-red-500 uppercase tracking-wide mb-1">Bugs</div>
              <div className="text-4xl font-bold text-red-700">{data.bugCount || 0}</div>
            </div>
            <div className="bg-orange-50 p-4 rounded-xl border-2 border-orange-200">
              <div className="text-xs font-semibold text-orange-500 uppercase tracking-wide mb-1">Dettes Tech.</div>
              <div className="text-4xl font-bold text-orange-700">{data.detteTechniqueCount || 0}</div>
            </div>
            <div className="bg-gray-50 p-4 rounded-xl border-2 border-gray-300">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Total</div>
              <div className="text-4xl font-bold text-gray-700">{data.totalCount || 0}</div>
            </div>
          </div>

          {/* Bar chart */}
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Répartition par type
          </h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData} margin={{ top: 5, right: 10, left: -15, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#6b7280' }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#6b7280' }} />
              <Tooltip
                cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                formatter={v => [v + ' work item' + (v > 1 ? 's' : ''), 'Complétés']}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
              />
              <Bar dataKey="count" radius={[5, 5, 0, 0]}>
                {chartData.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </>
      )}
    </div>
  );
}
